// Service for Monthly Cuts
import { apiClient, API_CONFIG } from '@/api/core';
import { buildQueryString } from '@/api/shared';
import type { ApiResponse } from '@/api/shared';
import type { PaginatedListSort } from '@/hooks/ui/usePaginatedList';
import type {
  LC_MonthlyCutoffOut,
  LC_GetMonthlyCutoffsIn,
  LC_GetAllMonthlyCutoffsOut,
  MonthlyCutCompanyScope,
  LC_GetMonthlyCutsOut,
  MonthlyCutsResult,
  LC_MonthlyCutsManagementListOut,
  LC_MonthlyCutsCountersOut,
  MonthlyCutsManagementListPage,
  MonthlyCutsManagementListParams,
  MonthlyCutsCountersParams,
  MonthlyCutsCounters,
  LC_CreateMonthlyCutIn,
  LC_CreateMonthlyCutOut,
  CreateMonthlyCutInput,
  LC_EditMonthlyCutIn,
  LC_EditMonthlyCutOut,
  EditMonthlyCutInput,
  LC_MonthlyCutDetailOut,
  MonthlyCutDetail,
  LC_MonthlyCutGroupsListOut,
  MonthlyCutGroupsPage,
  MonthlyCutGroupsParams,
} from '../types';
import {
  mapMonthlyCutsResult,
  mapMonthlyCutManagementItem,
  mapMonthlyCutsCounters,
  mapMonthlyCutGroupItem,
  mapMonthlyCutDetail,
} from '../mappers';

// Único valor de ordenamiento aceptado por el backend para las agrupaciones del corte.
const MONTHLY_CUT_GROUPS_ORDER_BY = 'groupName';


export interface MonthlyCutsExportParams {
  searchText?: string;
  sort?: PaginatedListSort;
  filterCompanyId?: string | null;
  year?: string | null;
  month?: string | null;
  state?: string | null;
}


export class MonthlyCutsService {
  async getMonthlyCutoffs(
    input: LC_GetMonthlyCutoffsIn,
  ): Promise<ApiResponse<LC_MonthlyCutoffOut[]>> {
    return apiClient.post<LC_MonthlyCutoffOut[]>(
      API_CONFIG.ENDPOINTS.ATTENDANCE.GET_MONTHLY_CUTOFFS,
      input,
    );
  }

  async getAllMonthlyCutoffs(
    companyScope?: MonthlyCutCompanyScope,
  ): Promise<ApiResponse<LC_GetAllMonthlyCutoffsOut>> {
    const queryString = buildQueryString({ companyScope });
    return apiClient.get<LC_GetAllMonthlyCutoffsOut>(
      `${API_CONFIG.ENDPOINTS.ATTENDANCE.GET_ALL_MONTHLY_CUTOFFS()}${queryString}`,
    );
  }

  async getMonthlyCutsNew(): Promise<ApiResponse<MonthlyCutsResult>> {
    const response = await apiClient.get<LC_GetMonthlyCutsOut>(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS,
    );
    if (!response.success || !response.data) {
      return {
        success: false,
        data: null,
        error: response.error,
        status: response.status,
      };
    }
    return {
      success: true,
      data: mapMonthlyCutsResult(response.data),
      status: response.status,
    };
  }
  async listManagement(
    params: MonthlyCutsManagementListParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutsManagementListPage>> {
    const queryString = buildQueryString({
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: params.sort?.key,
      orderDirection: params.sort?.direction?.toUpperCase(),
      filterCompanyId: params.filterCompanyId ?? undefined,
      year: params.year ?? undefined,
      month: params.month ?? undefined,
      state: params.state ?? undefined,
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT}${queryString}`;
    const response = await apiClient.get<LC_MonthlyCutsManagementListOut>(endpoint, { signal });

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          items: (response.data.data ?? []).map(mapMonthlyCutManagementItem),
          totalRecords: response.data.pagination.totalRecords,
        },
      };
    }

    return { success: false, data: null, error: response.error };
  }

  async getCounters(
    paramsOrSignal?: MonthlyCutsCountersParams | AbortSignal,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutsCounters>> {
    let params: MonthlyCutsCountersParams = {};
    let abortSignal: AbortSignal | undefined;

    if (paramsOrSignal instanceof AbortSignal) {
      abortSignal = paramsOrSignal;
    } else {
      params = paramsOrSignal ?? {};
      abortSignal = signal;
    }

    const queryString = buildQueryString({
      filterCompanyId: params.filterCompanyId ?? undefined,
      year: params.year ?? undefined,
      month: params.month ?? undefined,
      state: params.state ?? undefined,
    });
    const countersEndpoint =
      `${API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT_COUNTERS}${queryString}`;
    const response = await apiClient.get<LC_MonthlyCutsCountersOut>(
      countersEndpoint,
      { signal: abortSignal },
    );

    if (response.success && response.data) {
      return { success: true, data: mapMonthlyCutsCounters(response.data) };
    }

    return { success: false, data: null, error: response.error };
  }

  async create(
    input: CreateMonthlyCutInput,
  ): Promise<ApiResponse<LC_CreateMonthlyCutOut>> {
    const body: LC_CreateMonthlyCutIn = {
      year: input.year,
      month: input.month,
      // Las fechas ya llegan en ISO yyyy-MM-dd: el DatePicker las genera con
      // formatDateToYYYYMMDD (@/utils), el formato estándar de API del proyecto.
      startDate: input.startDate,
      endDate: input.endDate,
      groupIds: input.groupIds.length > 0 ? input.groupIds.join(',') : undefined,
      applyToAllCompanies: input.applyToAllCompanies,
      // targetCompanyId es requerido por el backend incluso en modo holding
      // (representa la empresa desde la que se solicita el corte).
      targetCompanyId: input.targetCompanyId ?? undefined,
    };

    return apiClient.post<LC_CreateMonthlyCutOut>(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT,
      body,
    );
  }

  async getDetail(
    id: number,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutDetail>> {
    const response = await apiClient.get<LC_MonthlyCutDetailOut>(
      `${API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT}/${id}`,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: mapMonthlyCutDetail(response.data),
        status: response.status,
      };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async update(
    input: EditMonthlyCutInput,
  ): Promise<ApiResponse<LC_EditMonthlyCutOut>> {
    const body: LC_EditMonthlyCutIn = {
      year: input.year,
      month: input.month,
      // Las fechas ya llegan en ISO yyyy-MM-dd: el DatePicker las genera con
      // formatDateToYYYYMMDD (@/utils), el formato estándar de API del proyecto.
      startDate: input.startDate,
      endDate: input.endDate,
      groupIds: input.groupIds.length > 0 ? input.groupIds.join(',') : undefined,
    };

    return apiClient.put<LC_EditMonthlyCutOut>(
      `${API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT}/${input.id}`,
      body,
    );
  }

  async getAvailableGroups(
    params: MonthlyCutGroupsParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutGroupsPage>> {
    return this.getGroups(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT_GROUPS_AVAILABLE,
      params,
      signal,
    );
  }

  async getAssignedGroups(
    params: MonthlyCutGroupsParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutGroupsPage>> {
    return this.getGroups(
      API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT_GROUPS_ASSIGNED,
      params,
      signal,
    );
  }

  private async getGroups(
    endpoint: string,
    params: MonthlyCutGroupsParams,
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCutGroupsPage>> {
    const queryString = buildQueryString({
      monthlyCutId: params.monthlyCutId,
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText,
      orderBy: MONTHLY_CUT_GROUPS_ORDER_BY,
      orderDirection: params.orderDirection ?? 'ASC',
    });
    const response = await apiClient.get<LC_MonthlyCutGroupsListOut>(
      `${endpoint}${queryString}`,
      { signal },
    );

    if (response.success && response.data) {
      return {
        success: true,
        data: {
          items: (response.data.data ?? []).map(mapMonthlyCutGroupItem),
          totalRecords: response.data.pagination.totalRecords,
        },
        status: response.status,
      };
    }

    return { success: false, data: null, error: response.error, status: response.status };
  }

  async exportFile(
    params?: MonthlyCutsExportParams,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    const queryString = buildQueryString({
      searchText: params?.searchText,
      orderBy: params?.sort?.key,
      orderDirection: params?.sort?.direction?.toUpperCase(),
      filterCompanyId: params?.filterCompanyId,
      year: params?.year,
      month: params?.month,
      state: params?.state,
    });
    return apiClient.downloadBlobWithFilename(
      `${API_CONFIG.ENDPOINTS.MONTHLY_CUTS_MANAGEMENT_EXPORT}${queryString}`,
      { method: 'GET' },
    );
  }
}

export const monthlyCutsService = new MonthlyCutsService();
